import React, { useState } from 'react';
import { FadeIn } from './ui/FadeIn';
import { CtaButton } from './ui/CtaButton';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-14">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-15">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25
      }} className="bg-brand-card border border-brand-gold/20 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-16">
            <div className="flex items-center justify-between p-6 border-b border-brand-bg" data-id="element-17">
              <h3 className="text-xl font-bold text-white" data-id="element-18">{title}</h3>
              <button onClick={onClose} className="text-brand-muted hover:text-white transition-colors p-1" data-id="element-19">
                <X className="w-5 h-5" data-id="element-20" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-brand-muted text-sm leading-relaxed space-y-4" data-id="element-21">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function FinalCtaSection() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  return <section className="w-full px-4 md:px-6 pb-8" data-id="element-22">
      <div className="max-w-3xl mx-auto" data-id="element-23">
        <FadeIn data-id="element-24">
          <div className="bg-gradient-to-b from-brand-card to-brand-bg border border-brand-gold rounded-2xl p-8 md:p-16 text-center shadow-2xl shadow-brand-gold/5 relative overflow-hidden" id="final-cta" data-id="element-25">
            {/* Background decorative glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-brand-gold/10 blur-[100px] pointer-events-none" data-id="element-26"></div>

            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif tracking-tight" data-id="element-27">
              MANUAL DEL DINERO
            </h2>

            <div className="flex items-center justify-center gap-4 mb-8" data-id="element-28">
              <span className="text-2xl md:text-3xl text-brand-muted line-through" data-id="element-29">
                US$19
              </span>
              <span className="text-5xl md:text-6xl font-bold text-brand-gold" data-id="element-30">
                US$6.39
              </span>
              <span className="bg-brand-gold text-brand-bg text-sm font-bold px-3 py-1.5 rounded-md" data-id="element-31">
                78% OFF
              </span>
            </div>

            <div className="space-y-2 text-brand-muted mb-10 text-lg" data-id="element-32">
              <p data-id="element-33">Acceso inmediato</p>
              <p data-id="element-34">Formato: ebook + plantillas + checklists + plan accionable</p>
            </div>

            <div className="max-w-md mx-auto mb-8" data-id="element-35">
              <CtaButton fullWidth onClick={() => window.open('https://pay.hotmart.com/F104344877Y', '_blank')} data-id="element-36">
                Descárgalo ahora y ejecuta el Día 1 hoy
              </CtaButton>
            </div>

            <p className="text-brand-text/80 italic text-sm md:text-base" data-id="element-37">
              No "cuando tengas tiempo". <br data-id="element-38" />
              Porque el desorden siempre cobra primero.
            </p>
          </div>

          <div className="mt-12 text-center text-brand-muted text-xs space-y-3" data-id="element-39">
            <div className="flex items-center justify-center gap-3" data-id="element-40">
              <button onClick={() => setActiveModal('privacy')} className="underline underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-41">
                Política de Privacidad
              </button>
              <span className="text-brand-muted/40" data-id="element-42">·</span>
              <button onClick={() => setActiveModal('terms')} className="underline underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-43">
                Términos y Condiciones
              </button>
            </div>
            <p data-id="element-44">
              © {new Date().getFullYear()} Manual del Dinero. Todos los
              derechos reservados.
            </p>
          </div>
        </FadeIn>
      </div>

      {/* Privacy Policy Modal */}
      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-45">
        <p data-id="element-46">
          <strong className="text-white" data-id="element-47">Última actualización:</strong> Febrero
          2026
        </p>
        <p data-id="element-48">
          En Manual del Dinero, respetamos tu privacidad y nos comprometemos a
          proteger tu información personal.
        </p>
        <p data-id="element-49">
          <strong className="text-white" data-id="element-50">1. Información que recopilamos</strong>
        </p>
        <p data-id="element-51">
          Recopilamos únicamente la información necesaria para procesar tu
          compra: nombre, correo electrónico y datos de pago. No almacenamos
          información de tarjetas de crédito directamente; esta es procesada por
          nuestro proveedor de pagos seguro.
        </p>
        <p data-id="element-52">
          <strong className="text-white" data-id="element-53">2. Uso de la información</strong>
        </p>
        <p data-id="element-54">
          Tu información se utiliza exclusivamente para: entregar el producto
          digital adquirido, enviar actualizaciones relacionadas con tu compra,
          y mejorar nuestros servicios.
        </p>
        <p data-id="element-55">
          <strong className="text-white" data-id="element-56">3. Compartir información</strong>
        </p>
        <p data-id="element-57">
          No vendemos, alquilamos ni compartimos tu información personal con
          terceros, excepto cuando sea necesario para procesar tu compra o
          cuando la ley lo requiera.
        </p>
        <p data-id="element-58">
          <strong className="text-white" data-id="element-59">4. Seguridad</strong>
        </p>
        <p data-id="element-60">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información contra acceso no autorizado, alteración o
          destrucción.
        </p>
        <p data-id="element-61">
          <strong className="text-white" data-id="element-62">5. Tus derechos</strong>
        </p>
        <p data-id="element-63">
          Puedes solicitar acceso, corrección o eliminación de tus datos
          personales en cualquier momento contactándonos directamente.
        </p>
        <p data-id="element-64">
          <strong className="text-white" data-id="element-65">6. Contacto</strong>
        </p>
        <p data-id="element-66">
          Para cualquier consulta sobre privacidad, puedes escribirnos y te
          responderemos en un plazo máximo de 48 horas.
        </p>
      </LegalModal>

      {/* Terms & Conditions Modal */}
      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-67">
        <p data-id="element-68">
          <strong className="text-white" data-id="element-69">Última actualización:</strong> Febrero
          2026
        </p>
        <p data-id="element-70">
          Al adquirir el Manual del Dinero, aceptas los siguientes términos y
          condiciones.
        </p>
        <p data-id="element-71">
          <strong className="text-white" data-id="element-72">1. Producto digital</strong>
        </p>
        <p data-id="element-73">
          El Manual del Dinero es un producto digital (ebook) que incluye
          plantillas, checklists y un plan accionable. Se entrega en formato
          digital inmediatamente después de la compra.
        </p>
        <p data-id="element-74">
          <strong className="text-white" data-id="element-75">2. Uso personal</strong>
        </p>
        <p data-id="element-76">
          El contenido adquirido es para uso personal y no puede ser
          redistribuido, revendido, compartido públicamente ni reproducido sin
          autorización expresa.
        </p>
        <p data-id="element-77">
          <strong className="text-white" data-id="element-78">3. Propiedad intelectual</strong>
        </p>
        <p data-id="element-79">
          Todo el contenido del Manual del Dinero, incluyendo textos,
          plantillas, gráficos y metodologías, está protegido por derechos de
          autor.
        </p>
        <p data-id="element-80">
          <strong className="text-white" data-id="element-81">4. Naturaleza educativa</strong>
        </p>
        <p data-id="element-82">
          Este producto tiene fines educativos e informativos. No constituye
          asesoría financiera profesional. Las decisiones financieras son
          responsabilidad exclusiva del comprador.
        </p>
        <p data-id="element-83">
          <strong className="text-white" data-id="element-84">5. Política de reembolso</strong>
        </p>
        <p data-id="element-85">
          Dado que se trata de un producto digital con entrega inmediata, no se
          ofrecen reembolsos una vez realizada la descarga. Si tienes problemas
          técnicos con la entrega, contáctanos y lo resolveremos.
        </p>
        <p data-id="element-86">
          <strong className="text-white" data-id="element-87">6. Modificaciones</strong>
        </p>
        <p data-id="element-88">
          Nos reservamos el derecho de modificar estos términos. Las
          modificaciones serán efectivas desde su publicación.
        </p>
      </LegalModal>
    </section>;
}